import { Button } from "@/components/ui/button";
import { ArrowRight, Play, X, Award, Users, ShieldCheck } from "lucide-react";
import { useState } from "react";
import certificationLogos from "@/assets/certification-logos.png";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import cimbLogo from "@/assets/logos/cimb-bank-white.png";
import lhBankLogo from "@/assets/logos/lh-bank.png";
import pwcLogo from "@/assets/logos/pwc.png";
import setLogo from "@/assets/logos/set-color.png";
import bridgestoneLogo from "@/assets/logos/bridgestone.png";
const HeroSection = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isThai = language === "th";
  const logos = [{
    name: isThai ? "ตลาดหลักทรัพย์แห่งประเทศไทย - พาร์ทเนอร์ Carbonwize" : "Stock Exchange of Thailand - Carbonwize trusted partner",
    src: setLogo
  }, {
    name: isThai ? "CIMB Bank - ลูกค้าซอฟต์แวร์คาร์บอน" : "CIMB Bank - Carbon accounting software client",
    src: cimbLogo
  }, {
    name: isThai ? "LH Bank - ลูกค้าระบบจัดการคาร์บอน" : "LH Bank - Carbon management platform client",
    src: lhBankLogo
  }, {
    name: isThai ? "Bridgestone - ลูกค้าองค์กรด้านความยั่งยืน" : "Bridgestone - Enterprise sustainability client",
    src: bridgestoneLogo
  }];
  return <>
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
        {/* Background */}
        <div className="absolute inset-0 bg-[#024241]" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(hsl(var(--primary-foreground) / 0.1) 1px, transparent 1px),
                             linear-gradient(90deg, hsl(var(--primary-foreground) / 0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        </div>

        {/* Floating Elements */}
        <div className="absolute top-1/4 left-10 w-64 h-64 bg-accent/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-primary/30 rounded-full blur-3xl animate-pulse-slow animation-delay-400" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid xl:grid-cols-2 gap-12 xl:gap-16 items-center">
            {/* Content */}
            <div className="text-center xl:text-left pt-8 lg:pt-12">

              <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-primary-foreground mb-4 sm:mb-6 animate-fade-up animation-delay-200 ${isThai ? "font-thai leading-[1.4]" : "leading-tight"}`}>
                <span className="md:whitespace-nowrap">{t("hero.title1")}</span>
                <span className={`block md:whitespace-nowrap ${isThai ? "mt-2 sm:mt-4" : "mt-1 sm:mt-2"} text-accent`}>{t("hero.title2")}</span>
              </h1>

              <p className={`text-base sm:text-lg md:text-xl lg:text-2xl text-primary-foreground/80 max-w-xl mx-auto xl:mx-0 mb-4 sm:mb-6 animate-fade-up animation-delay-400 ${isThai ? "font-thai leading-relaxed" : ""}`}>
                {t("hero.description")}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 mb-4 sm:mb-6 animate-fade-up animation-delay-500 justify-center xl:justify-start">
                <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg group border-2 border-accent !rounded-full" onClick={() => window.location.href = '/contact-us'}>
                  {t("hero.cta")}
                  <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button size="lg" variant="ghost" className="text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/5 border-2 border-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg !rounded-full" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({
                behavior: 'smooth'
              })}>
                  {isThai ? "ดูวิธีการใช้งาน" : "See How It Works"}
                </Button>
              </div>

              {/* Trust Indicators - Badge Cards */}
              <div className="flex flex-wrap items-stretch gap-2 sm:gap-3 mb-6 sm:mb-8 animate-fade-up animation-delay-600 justify-center xl:justify-start">
                {/* Standards Compliance Badge */}
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl bg-primary-foreground/10 backdrop-blur-sm">
                  <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground flex-shrink-0" />
                  <span className={`text-xs sm:text-sm font-medium text-primary-foreground leading-tight ${isThai ? "font-thai" : ""}`}>
                    {isThai ? "รองรับมาตรฐานสากล" : "Global Standards"}
                  </span>
                </div>

                {/* Trusted Badge */}
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl bg-primary-foreground/10 backdrop-blur-sm">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground flex-shrink-0" />
                  <span className={`text-xs sm:text-sm font-medium text-primary-foreground leading-tight ${isThai ? "font-thai" : ""}`}>
                    {isThai ? "500+ องค์กร" : "500+ Enterprises"}
                  </span>
                </div>

                {/* SET Official Badge */}
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl bg-primary-foreground/10 backdrop-blur-sm">
                  <Award className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground flex-shrink-0" />
                  <span className={`text-xs sm:text-sm font-medium text-primary-foreground leading-tight ${isThai ? "font-thai" : ""}`}>
                    {isThai ? "พาร์ทเนอร์กับ SET" : "Official Partner with SET"}
                  </span>
                </div>
              </div>
            </div>

            {/* Video Thumbnail */}
            <div className="relative animate-scale-in animation-delay-400">
              <div className="absolute inset-4 bg-gradient-primary rounded-2xl blur-xl opacity-20 animate-pulse-slow" />
              <button 
                type="button"
                onClick={() => setIsVideoOpen(true)}
                aria-label={isThai ? "เล่นวิดีโอสาธิต Carbonwize" : "Play Carbonwize demo video"}
                className="relative bg-card/95 backdrop-blur-xl rounded-3xl overflow-hidden shadow-card border border-border/50 cursor-pointer group w-full text-left"
              >
                <div className="aspect-video relative">
                  <img 
                    src="https://img.youtube.com/vi/el0smAXCkdw/maxresdefault.jpg" 
                    alt={isThai ? "วิดีโอสาธิตแพลตฟอร์ม Carbonwize - ซอฟต์แวร์จัดการคาร์บอนสำหรับองค์กร" : "Carbonwize platform demo video - Enterprise carbon management software demonstration"} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    fetchPriority="high"
                    width={1280}
                    height={720}
                  />
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                    <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-primary-foreground fill-primary-foreground ml-1" aria-hidden="true" />
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Trusted By Section - Integrated */}
          <div className="mt-16 pb-8 animate-fade-up animation-delay-800">
            
            
            <div className="relative overflow-hidden">
              <div className="flex animate-scroll-left items-center">
              {/* First set of logos */}
                {logos.map((logo, index) => <div key={`first-${index}`} className="flex-shrink-0 mx-6 sm:mx-12 flex items-center justify-center px-3 sm:px-6">
                    <img src={logo.src} alt={logo.name} className={`w-auto object-contain opacity-70 hover:opacity-100 transition-all duration-300 grayscale brightness-0 invert ${logo.name.includes("Stock Exchange") || logo.name.includes("ตลาดหลักทรัพย์") ? "h-7 sm:h-9" : logo.name === "CIMB Bank" ? "h-6 sm:h-8" : "h-5 sm:h-7"}`} />
                  </div>)}
                {/* Duplicate set for seamless loop */}
                {logos.map((logo, index) => <div key={`second-${index}`} className="flex-shrink-0 mx-6 sm:mx-12 flex items-center justify-center px-3 sm:px-6">
                    <img src={logo.src} alt={logo.name} className={`w-auto object-contain opacity-70 hover:opacity-100 transition-all duration-300 grayscale brightness-0 invert ${logo.name.includes("Stock Exchange") || logo.name.includes("ตลาดหลักทรัพย์") ? "h-7 sm:h-9" : logo.name === "CIMB Bank" ? "h-6 sm:h-8" : "h-5 sm:h-7"}`} />
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
        <DialogContent className="max-w-4xl w-[90vw] p-0 bg-black border-none overflow-hidden">
          <DialogClose className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 hover:bg-white/20 transition-colors" aria-label={isThai ? "ปิดวิดีโอ" : "Close video"}>
            <X className="h-5 w-5 text-white" aria-hidden="true" />
          </DialogClose>
          <div className="aspect-video">
            {isVideoOpen && <iframe src="https://www.youtube.com/embed/el0smAXCkdw?autoplay=1&rel=0&modestbranding=1" title="Carbonwize Demo Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />}
          </div>
        </DialogContent>
      </Dialog>
    </>;
};
export default HeroSection;